import URLS from '@/store/urls';
import notificationWithIcon from '@/utils/notification';
import apiCall from '@/store/apiCall';
import createUploadFile from '@/utils/createUploadFile';
import uploadFile from '@/utils/uploadFile';
import types from './types';

const actions = {
  async getCategories({ commit }) {
    try {
      commit(types.GET_CATEGORIES_START);
      const { data: { data: categoriesData } } = await apiCall({
        type: 'GET',
        url: `${URLS.categoriesArticle}`,
        isToken: true,
      });
      commit(types.GET_CATEGORIES_SUCCESS, categoriesData);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
      commit(types.GET_CATEGORIES_FAILURE, message);
    }
  },
  async addOrEditCategory({ dispatch }, {
    id,
    isNew,
    values,
    desktopImage,
    mobileImage,
    callback,
  }) {
    try {
      const getImageUrl = async (uploadingFile) => {
        const { url, file } = uploadingFile;
        if (file) {
          const {
            data: {
              data: uploadedFile,
            },
          } = await createUploadFile(url, 'images-blog');
          return uploadedFile.url;
        }
        return uploadingFile.url;
      };
      const imageUrl = desktopImage.length
        ? await getImageUrl(desktopImage[0])
        : null;
      const mobileImageUrl = mobileImage.length
        ? await getImageUrl(mobileImage[0])
        : null;

      await apiCall({
        type: isNew ? 'POST' : 'PUT',
        url: isNew ? URLS.categoriesArticle : `${URLS.categoriesArticle}/${id}`,
        isToken: true,
        body: {
          ...values,
          imageUrl,
          mobileImageUrl,
        },
      });
      notificationWithIcon('success', `Category ${isNew ? 'created' : 'updated'}`);
      if (callback) {
        callback();
      }
      dispatch('getCategories');
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async editCategoryOrderIndex({ dispatch }, payload) {
    try {
      await apiCall({
        type: 'PUT',
        url: `${URLS.categoriesArticle}/change/position`,
        body: payload,
        isToken: true,
      });
      notificationWithIcon('success', 'Positions updated');
      dispatch('getCategories');
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async onDeleteCategory({ dispatch }, id) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.categoriesArticle}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Category deleted');
      dispatch('getCategories');
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async getArticles({ commit }, payload) {
    try {
      commit(types.GET_ARTICLES_START);
      const { limit, offset } = payload;
      const { data: { data: articlesData } } = await apiCall({
        type: 'GET',
        url: `${URLS.article}`,
        isToken: true,
        params: {
          pagination: {
            limit,
            offset,
          },
        },
      });
      commit(types.GET_ARTICLES_SUCCESS, articlesData);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
      commit(types.GET_ARTICLES_FAILURE, message);
    }
  },
  async getAllBannerAds({ commit }) {
    commit(types.GET_ALL_BANNER_ADS_START);
    try {
      const { data: { data: articlesData } } = await apiCall({
        type: 'GET',
        url: `${URLS.article}`,
        isToken: true,
        params: {
          pagination: {
            limit: 1000,
            offset: 0,
          },
        },
      });

      const newData = await Promise.allSettled((articlesData.data || []).map((itm) => (
        apiCall({
          type: 'GET',
          url: `${URLS.articleBanner}/${itm.id}`,
          isToken: true,
        })
      )));

      const newSortData = (articlesData.data || []).reduce((acc, item, index) => {
        const { category, categoryId } = item;
        const { status, value: adsRes } = newData[index];

        if (status !== 'fulfilled') {
          return acc;
        }

        const { data: { data: { banner } } } = adsRes || {};

        if (!banner) {
          return acc;
        }

        if (!acc[categoryId]) {
          return { ...acc, [categoryId]: { category, categoryId, banners: [...(banner || [])] } };
        }
        return {
          ...acc,
          [categoryId]: {
            ...acc[categoryId],
            banners: [...acc[categoryId].banners, ...(banner || [])],
          },
        };
      }, {});
      commit(types.GET_ALL_BANNER_ADS_SUCCESS, newSortData);
    } catch (e) {
      commit(types.GET_ALL_BANNER_ADS_FAILURE, e);
    }
  },
  async onDeleteArticle({ dispatch }, payload) {
    try {
      const { id, limit, offset } = payload;
      await apiCall({
        type: 'DELETE',
        url: `${URLS.article}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Article deleted');
      dispatch('getArticles', { limit, offset });
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async getArticle({ commit }, payload) {
    try {
      commit(types.GET_ARTICLE_START);
      const { data: { data: article } } = await apiCall({
        type: 'GET',
        url: `${URLS.article}/${payload}`,
        isToken: true,
      });
      commit(types.GET_ARTICLE_SUCCESS, article);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
      commit(types.GET_ARTICLE_FAILURE, message);
    }
  },
  async createOrUpdateArticle({ commit, dispatch }, payload) {
    const {
      id,
      images,
      isNew,
      values,
      callBack,
    } = payload;
    try {
      commit(types.SUBMIT_ARTICLE_START);
      const imagesData = await Promise.all(
        Object.values(images).map((value) => {
          const { file } = value;
          if (file) {
            return uploadFile(file, 'images-blog');
          }
          return value;
        }),
      );
      await apiCall({
        type: isNew ? 'POST' : 'PUT',
        url: isNew ? URLS.article : `${URLS.article}/${id}`,
        isToken: true,
        body: {
          ...values,
          imageUri: imagesData[0]?.url || imagesData[0]?.data?.data?.url || '',
          mobileImageUrl: imagesData[1]?.url || imagesData[1]?.data?.data?.url || '',
          previewImageUri: imagesData[2]?.url || imagesData[2]?.data?.data?.url || '',
        },
      });
      notificationWithIcon('success', `Category ${isNew ? 'created' : 'updated'}`);
      dispatch('getArticle', id);
      if (callBack) {
        callBack();
      }
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
      commit(types.SUBMIT_ARTICLE_FAILURE, message);
    }
  },
  async getArticleBannerAds({ commit }, payload) {
    try {
      commit(types.GET_ARTICLE_BANNER_START);
      const { data: { data: { banner } } } = await apiCall({
        type: 'GET',
        url: `${URLS.articleBanner}/${payload}`,
        isToken: true,
      });
      commit(types.GET_ARTICLE_BANNER_SUCCESS, banner);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
      commit(types.GET_ARTICLE_BANNER_FAILURE, message);
    }
  },
  async deleteArticleBanner({ dispatch }, { id, articleId }) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.articleBanner}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Article banner deleted');
      dispatch('getArticleBannerAds', articleId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async editArticleBannerOrder({ dispatch }, { articleId, banners }) {
    try {
      await apiCall({
        type: 'PATCH',
        url: `${URLS.articleBanner}/change-position`,
        body: banners,
        isToken: true,
      });
      notificationWithIcon('success', 'Article banner deleted');
      dispatch('getArticleBannerAds', articleId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },
  async createOrEditArticleBanner(
    { commit, dispatch },
    {
      articleId,
      values,
      editableBannerId,
      image,
      callback,
    },
  ) {
    try {
      commit(types.SUBMIT_ARTICLE_BANNER_START);
      const isNew = typeof editableBannerId !== 'number';
      let imageUrl = '';

      const { file, url } = image;
      if (file) {
        const {
          data: {
            data: {
              url: UploadedUrl,
            },
          },
        } = await uploadFile(file, 'images-blog');
        imageUrl = UploadedUrl;
      } else {
        imageUrl = url || '';
      }
      await apiCall({
        type: isNew ? 'POST' : 'PUT',
        url: isNew ? URLS.articleBanner : `${URLS.articleBanner}/${editableBannerId}`,
        isToken: true,
        body: { ...values, imageUrl },
      });
      notificationWithIcon('success', `Article banner ${isNew ? 'created' : 'updated'}`);
      dispatch('getArticleBannerAds', articleId);
      if (callback) {
        callback();
      }
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
      commit(types.SUBMIT_ARTICLE_BANNER_FAILURE, message);
    }
  },
};

export default actions;
